/* onboardingKit.tsx — shared primitives for the Onlymind onboarding stage.
   Ported from the Claude Design prototype (onboarding-app.jsx), typed for TS. */
import React from 'react';
import { Lockup } from '../../brand/Brand';

export const C = {
  bg: 'var(--om-page)', panel: 'color-mix(in srgb, var(--om-card) 94%, transparent)', panelSolid: 'var(--om-card)',
  line: 'color-mix(in srgb, var(--om-ink) 9%, transparent)', line2: 'color-mix(in srgb, var(--om-ink) 16%, transparent)',
  purple: 'var(--om-accent)', purpleHi: 'var(--om-accent-hover)', text: 'var(--om-text)', muted: 'var(--om-sub)', faint: 'var(--om-muted)',
} as const;

export type Pose = 'wave' | 'explain' | 'think' | 'point' | 'okay';
export const POSE: Record<Pose, string> = {
  wave: '/onboarding/sofi-wave.webp',
  explain: '/onboarding/sofi-explain.webp',
  think: '/onboarding/sofi-think.webp',
  point: '/onboarding/sofi-point.webp',
  okay: '/onboarding/sofi-okay.webp',
};

/* ---------- icons ---------- */
type IconName =
  | 'globe' | 'crown' | 'users' | 'plus' | 'download' | 'key' | 'check' | 'chat'
  | 'ladder' | 'gift' | 'convert' | 'copy' | 'help' | 'arrow' | 'refresh';

export function Icon({ name, size = 22, color = 'currentColor' }: { name: IconName; size?: number; color?: string }) {
  const p: Record<IconName, React.ReactNode> = {
    globe: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18" /></>,
    crown: <path d="M3 8l4 3 5-6 5 6 4-3-2 11H5L3 8z" />,
    users: <><circle cx="9" cy="8" r="3.2" /><path d="M3.5 19c.6-3 2.8-4.5 5.5-4.5S14 16 14.5 19" /><path d="M16 6.2a3 3 0 0 1 0 5.6M17.5 19c-.2-1.6-.8-2.9-1.8-3.8" /></>,
    plus: <path d="M12 5v14M5 12h14" />,
    download: <><path d="M12 4v11M7 11l5 5 5-5" /><path d="M5 19h14" /></>,
    key: <><circle cx="8" cy="9" r="3.5" /><path d="M10.5 11.5L19 20M16 17l2-2M14 15l2-2" /></>,
    check: <path d="M5 13l4 4 10-11" />,
    chat: <path d="M5 6h14v9H9l-4 4V6z" />,
    ladder: <><path d="M7 3v18M17 3v18M7 7h10M7 12h10M7 17h10" /></>,
    gift: <><path d="M5 11h14v8H5z" /><path d="M4 7h16v4H4zM12 7v12M12 7C9 7 8 3 12 3M12 7c3 0 4-4 0-4" /></>,
    convert: <><path d="M4 8h11l-3-3M20 16H9l3 3" /></>,
    copy: <><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h8" /></>,
    help: <><circle cx="12" cy="12" r="9" /><path d="M9.2 9.3a2.8 2.8 0 0 1 5.3 1c0 1.8-2.7 2.2-2.7 4" /><path d="M12 17.3v.01" /></>,
    arrow: <path d="M5 12h14M13 6l6 6-6 6" />,
    refresh: <><path d="M4 9a8 8 0 0 1 13-3l3 3M20 15a8 8 0 0 1-13 3l-3-3" /><path d="M20 4v5h-5M4 20v-5h5" /></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {p[name]}
    </svg>
  );
}

/* ---------- buttons ---------- */
type BtnProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  kind?: 'primary' | 'ghost' | 'soft';
  wide?: boolean;
  icon?: IconName;
};
export function Btn({ children, kind = 'primary', wide, icon, style, ...p }: BtnProps) {
  const base: React.CSSProperties = {
    fontFamily: 'inherit', fontWeight: 700, fontSize: 15, borderRadius: 12, padding: '13px 22px', cursor: 'pointer',
    border: '1px solid transparent', width: wide ? '100%' : 'auto',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9,
  };
  const styles: Record<string, React.CSSProperties> = {
    primary: { background: C.purple, color: '#fff', boxShadow: '0 10px 26px color-mix(in srgb, var(--om-accent) 40%, transparent)' },
    ghost: { background: 'transparent', color: C.muted, border: `1px solid ${C.line2}` },
    soft: { background: 'color-mix(in srgb, var(--om-accent) 14%, transparent)', color: C.purpleHi, border: '1px solid color-mix(in srgb, var(--om-accent) 32%, transparent)' },
  };
  return (
    <button className="om-btn" style={{ ...base, ...styles[kind], ...style }} {...p}>
      {children}{icon && <Icon name={icon} size={18} />}
    </button>
  );
}

/* ---------- option (selectable) ---------- */
export function iconBox(sel: boolean): React.CSSProperties {
  return {
    width: 42, height: 42, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0, transition: 'all .16s',
    background: sel ? 'color-mix(in srgb, var(--om-accent) 22%, transparent)' : 'color-mix(in srgb, var(--om-ink) 6%, transparent)', color: sel ? C.purpleHi : C.muted,
  };
}
export function Radio({ sel }: { sel: boolean }) {
  return (
    <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${sel ? C.purple : C.line2}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all .16s' }}>
      {sel && <div style={{ width: 10, height: 10, borderRadius: '50%', background: C.purple, animation: 'om-pop .2s ease' }} />}
    </div>
  );
}
export function Option({ icon, title, desc, selected, onClick, badge, layout = 'card' }: {
  icon?: React.ReactNode; title: string; desc?: string; selected?: boolean; onClick?: () => void; badge?: string; layout?: 'card' | 'list';
}) {
  const sel = !!selected;
  const ring = sel ? '0 0 0 3px color-mix(in srgb, var(--om-accent) 16%, transparent)' : 'none';
  const common: React.CSSProperties = {
    cursor: 'pointer', transition: 'all .16s', boxShadow: ring,
    background: sel ? 'color-mix(in srgb, var(--om-accent) 13%, transparent)' : 'color-mix(in srgb, var(--om-ink) 3.5%, transparent)', border: `1px solid ${sel ? 'color-mix(in srgb, var(--om-accent) 60%, transparent)' : C.line}`,
  };
  if (layout === 'list') {
    return (
      <div className="om-opt" onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '15px 18px', borderRadius: 14, ...common }}>
        {icon && <div style={iconBox(sel)}>{icon}</div>}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: C.text, fontSize: 15.5 }}>{title}</div>
          {desc && <div style={{ color: C.muted, fontSize: 13.5, marginTop: 3 }}>{desc}</div>}
        </div>
        <Radio sel={sel} />
      </div>
    );
  }
  return (
    <div className="om-opt" onClick={onClick} style={{ padding: 20, borderRadius: 16, position: 'relative', ...common }}>
      {badge && <div style={{ position: 'absolute', top: 16, right: 16, fontSize: 11, fontWeight: 700, color: C.purpleHi, background: 'color-mix(in srgb, var(--om-accent) 16%, transparent)', padding: '3px 9px', borderRadius: 999 }}>{badge}</div>}
      {icon && <div style={{ ...iconBox(sel), marginBottom: 14 }}>{icon}</div>}
      <div style={{ fontWeight: 700, color: C.text, fontSize: 16 }}>{title}</div>
      {desc && <div style={{ color: C.muted, fontSize: 13.5, marginTop: 6, lineHeight: 1.5 }}>{desc}</div>}
    </div>
  );
}

/* ---------- progress dots ---------- */
export function Dots({ n, i }: { n: number; i: number }) {
  return (
    <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
      {Array.from({ length: n }).map((_, k) => (
        <div key={k} style={{
          height: 6, borderRadius: 999, transition: 'all .3s cubic-bezier(.4,0,.2,1)',
          width: k === i ? 26 : 6, background: k === i ? C.purple : (k < i ? 'color-mix(in srgb, var(--om-accent) 50%, transparent)' : C.line2),
        }} />
      ))}
    </div>
  );
}

/* ---------- header / title ---------- */
export function Head({ n, i, label, onBack }: { n: number; i: number; label?: string; onBack?: (() => void) | null }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {onBack && (
          <button className="om-btn" onClick={onBack} aria-label="Back" style={{ width: 30, height: 30, padding: 0, borderRadius: 9, background: 'color-mix(in srgb, var(--om-ink) 5%, transparent)', border: `1px solid ${C.line2}`, color: C.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ display: 'inline-flex', transform: 'rotate(180deg)' }}><Icon name="arrow" size={16} /></span>
          </button>
        )}
        <Lockup height={13} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {label && <span style={{ color: C.faint, fontSize: 12.5, fontWeight: 600 }}>{label}</span>}
        <Dots n={n} i={i} />
      </div>
    </div>
  );
}
export function Title({ k, h, sub }: { k?: string; h: string; sub?: string }) {
  return (
    <div style={{ marginBottom: 24 }}>
      {k && <div style={{ color: C.purpleHi, fontWeight: 700, fontSize: 13, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 10 }}>{k}</div>}
      <h1 style={{ margin: 0, color: C.text, fontSize: 27, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.18 }}>{h}</h1>
      {sub && <p style={{ margin: '11px 0 0', color: C.muted, fontSize: 15, lineHeight: 1.55, maxWidth: 440 }}>{sub}</p>}
    </div>
  );
}

/* ---------- panel shell (animated on key change) ---------- */
export function Panel({ children, width = 560, anim }: { children: React.ReactNode; width?: number; anim?: React.Key }) {
  return (
    <div key={anim} className="om-panel" style={{
      width, maxWidth: '100%', background: C.panel, border: `1px solid ${C.line}`,
      borderRadius: 24, boxShadow: '0 40px 120px rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)', padding: 36, position: 'relative',
    }}>
      {children}
      <div style={{ position: 'absolute', right: 40, bottom: -10, width: 22, height: 22, background: C.panel, borderRight: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}`, transform: 'rotate(45deg)' }} />
    </div>
  );
}
